// Auth Store - Zustand store with persistence
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '@/types/models/user.types';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthActions {
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  setLoading: (loading: boolean) => void;
  login: (user: User, token: string) => void;
  logout: () => void;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      // State
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,

      // Actions
      setUser: (user) =>
        set({
          user,
          isAuthenticated: !!user,
        }),

      setToken: (token) => {
        // Also update localStorage for API client
        if (token) {
          localStorage.setItem('carleads_auth_token', token);
        } else {
          localStorage.removeItem('carleads_auth_token');
        }
        set({ token });
      },

      setLoading: (loading) => set({ isLoading: loading }),

      login: (user, token) => {
        localStorage.setItem('carleads_auth_token', token);
        set({
          user,
          token,
          isAuthenticated: true,
        });
      },

      logout: () => {
        localStorage.removeItem('carleads_auth_token');
        set({
          user: null,
          token: null,
          isAuthenticated: false,
        });
      },
    }),
    {
      name: 'carleads-auth',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
